import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

// Vimeo Player SDK
import Player from "@vimeo/player";

// Toolkit
import { ThemeContext } from "sj-ui-toolkit/src/theme/ThemeContext";

import {
  fetchUserVideoSettings,
  updateUserAttributes,
  updateWatchedVideo,
  VideoSettings,
} from "sj-ui-toolkit/src/api";

import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";

import { VideoContext } from "../../VideoContext";
import S from "./_styles.module.scss";

const RESUME_KEY_PREFIX = "videoResume";
const SAVE_INTERVAL_MS = 3000;
const END_THRESHOLD_SECONDS = 5;

const readResumePosition = (key: string): number | null => {
  try {
    const value = Number(localStorage.getItem(key));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

const writeResumePosition = (key: string, seconds: number) => {
  try {
    localStorage.setItem(key, String(seconds));
  } catch {
    // Storage unavailable or full; resuming is best-effort
  }
};

const clearResumePosition = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage unavailable; nothing to clear
  }
};

interface VideoPlayerProps {
  videoId: string;
  lessonId: number;
  watchedVideo: boolean;
  assessmentId: number;
}

const VideoPlayer = ({ videoId, lessonId, watchedVideo, assessmentId }: VideoPlayerProps) => {
  const { appliedTheme } = useContext(ThemeContext);
  const { isLessonComplete } = useContext(VideoContext);
  const { user } = useUserContext();
  const resumeKey = user?.userId
    ? `${RESUME_KEY_PREFIX}:${user.userId}:${lessonId}:${videoId}`
    : null;
  // Ref so completing the lesson doesn't recreate the player
  const isLessonCompleteRef = useRef(isLessonComplete);
  isLessonCompleteRef.current = isLessonComplete;
  const playerRef = useRef<HTMLDivElement>(null);
  const watchedRef = useRef(watchedVideo);
  const lastPercentRef = useRef(0);
  const progressPercentRef = useRef(0);
  const videoSettingsRef = useRef<VideoSettings | null>(null);
  const volumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // hard-code colors for now
  const colors = useMemo<[string, string, string, string]>(() => {
    return appliedTheme === "atlas-light"
      ? ["eeeef0", "3b82f6", "1e2126", "eeeef0"]
      : ["282d33", "3b82f6", "ffffff", "282d33"];
  }, [appliedTheme]);

  // Small, continuous differences indicate normal playback so we count towards watch progress
  // Big jumps don't count towards progress, but we still update lastPercent so they can start accruing progress again
  // Rewinds are ignored so that can't simply watch the same segments over and over to reach 80%
  const handleTimeUpdate = useCallback(
    (data: { percent: number }) => {
      if (watchedRef.current) return;

      const currentPercent = data.percent;
      const difference = currentPercent - lastPercentRef.current;

      if (difference > 0 && difference < 0.06) {
        lastPercentRef.current = currentPercent;
        progressPercentRef.current += difference;

        if (progressPercentRef.current >= 0.8) {
          watchedRef.current = true;
          // updateWatchedVideo types levelId as required + ids as string, but callers only have
          // the numeric lessonId and the backend tolerates the missing levelId. TODO(T12): loosen.
          updateWatchedVideo({
            lessonId: String(lessonId),
            assessmentId: String(assessmentId),
          } as { levelId: string; lessonId: string; assessmentId: string });
        }
      } else if (difference >= 0.06) {
        lastPercentRef.current = currentPercent;
      }
    },
    [lessonId, assessmentId]
  );

  useEffect(() => {
    if (resumeKey && isLessonComplete) clearResumePosition(resumeKey);
  }, [resumeKey, isLessonComplete]);

  useEffect(() => {
    if (!videoId || !playerRef.current) return;

    const player = new Player(playerRef.current, {
      id: videoId,
      colors: colors,
    });

    fetchUserVideoSettings().then(settings => {
      if (settings) {
        player.setVolume(settings.volume || 1.0);
        player.setPlaybackRate(settings.playbackRate || 1.0);
        if (settings.textTrack) {
          player.enableTextTrack(settings.textTrack);
        }
      }
      videoSettingsRef.current = settings;
    });

    player.on("volumechange", data => {
      if (volumeTimeoutRef.current) clearTimeout(volumeTimeoutRef.current);
      volumeTimeoutRef.current = setTimeout(() => {
        updateUserAttributes("videoSettings", {
          ...(videoSettingsRef.current ?? {}),
          volume: data.volume,
        });
      }, 250);
    });

    player.on("playbackratechange", data => {
      updateUserAttributes("videoSettings", {
        ...(videoSettingsRef.current ?? {}),
        playbackRate: data.playbackRate,
      });
    });

    player.on("texttrackchange", data => {
      updateUserAttributes("videoSettings", {
        ...(videoSettingsRef.current ?? {}),
        textTrack: data.language,
      });
    });

    let latest: { seconds: number; duration: number } | null = null;
    let lastSavedAt = Date.now();
    const savedPosition =
      resumeKey && !isLessonCompleteRef.current ? readResumePosition(resumeKey) : null;
    // Hold off saving until the seek finishes so an early timeupdate can't overwrite the saved spot
    let canSave = !savedPosition;

    if (savedPosition) {
      player
        .getDuration()
        .then(duration => {
          if (duration - savedPosition > END_THRESHOLD_SECONDS) {
            // Keeps a short resume jump from counting as watch progress
            lastPercentRef.current = savedPosition / duration;
            return player.setCurrentTime(savedPosition);
          }
          if (resumeKey) clearResumePosition(resumeKey);
        })
        .catch(() => {})
        .finally(() => {
          canSave = true;
        });
    }

    player.on("timeupdate", data => {
      handleTimeUpdate(data);

      if (!resumeKey || !canSave || isLessonCompleteRef.current) return;
      const { seconds, duration } = data;

      if (duration - seconds <= END_THRESHOLD_SECONDS) {
        latest = null;
        clearResumePosition(resumeKey);
        return;
      }

      latest = { seconds, duration };
      const now = Date.now();
      if (now - lastSavedAt >= SAVE_INTERVAL_MS) {
        lastSavedAt = now;
        writeResumePosition(resumeKey, seconds);
      }
    });

    if (resumeKey) {
      player.on("ended", () => {
        latest = null;
        clearResumePosition(resumeKey);
      });
    }

    return () => {
      if (resumeKey && latest && !isLessonCompleteRef.current) {
        writeResumePosition(resumeKey, latest.seconds);
      }
      if (volumeTimeoutRef.current) clearTimeout(volumeTimeoutRef.current);
      player.destroy();
    };
  }, [videoId, handleTimeUpdate, resumeKey]);

  return <div className={S.frameContainer} ref={playerRef} data-testid="video-player"></div>;
};

export default VideoPlayer;
